import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import {
  applyForMembership,
  getMyMembershipStatus,
  initializeMembershipPayment,
} from '../services/membershipService';
import './Membership.css';

const Membership = () => {
  const { isLoggedIn } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [membershipStatus, setMembershipStatus] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const [formData, setFormData] = useState({
    type: 1, // 1 = SINGLE, 2 = COUPLE
    authCapture: true, // Always true - payment before admin approval
    name: '',
    phone: '',
    email: '',
    socialMediaLink: '',
    addressStreet: '',
    addressNeighborhood: '',
    addressMoreInfo: '',
    residenceCountry: '',
    birthdate: '',
    gender: 1, // 1 = MALE, 2 = FEMALE
    // Couple fields (only if type === 2)
    coupleName: '',
    couplePhone: '',
    coupleSocialMediaLink: '',
  });

  const [error, setError] = useState('');

  useEffect(() => {
    if (!isLoggedIn) {
      navigate('/login');
      return;
    }
    fetchMembershipStatus();
  }, [isLoggedIn, navigate]);

  const fetchMembershipStatus = async () => {
    try {
      setLoading(true);
      const data = await getMyMembershipStatus();
      setMembershipStatus(data);
      // Show form if user can apply OR if there's no membership at all
      setShowForm(data.canApply === true || (data.status === 'none' && !data.hasMembership));
    } catch (err) {
      console.error('Error fetching membership status:', err);
      setError('Failed to load membership status');
      // On error, show the form anyway so user can try to apply
      setShowForm(true);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handlePhoneChange = (value, field) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.name || !formData.phone || !formData.email) {
      setError('Please fill in all required fields');
      return;
    }

    if (formData.type === 2 && (!formData.coupleName || !formData.couplePhone)) {
      setError('Please fill in couple information');
      return;
    }

    try {
      setSubmitting(true);

      // Prepare data by removing empty optional fields
      const submitData = { ...formData };

      // Remove empty optional fields to avoid validation errors
      if (!submitData.birthdate) delete submitData.birthdate;
      if (!submitData.socialMediaLink) delete submitData.socialMediaLink;
      if (!submitData.addressStreet) delete submitData.addressStreet;
      if (!submitData.addressNeighborhood) delete submitData.addressNeighborhood;
      if (!submitData.addressMoreInfo) delete submitData.addressMoreInfo;
      if (!submitData.residenceCountry) delete submitData.residenceCountry;
      if (!submitData.coupleSocialMediaLink) delete submitData.coupleSocialMediaLink;

      console.log('Submitting membership application...', submitData);
      const response = await applyForMembership(submitData);
      console.log('Application response:', response);

      // Always redirect to payment (auth/capture flow is mandatory)
      if (response.paymentUrl) {
        console.log('Redirecting to payment URL:', response.paymentUrl);
        window.location.href = response.paymentUrl;
        return;
      }

      // If no payment URL (shouldn't happen), show error
      setError('Payment initialization failed. Please try again or contact support.');
    } catch (err) {
      console.error('Membership application error:', err);
      console.error('Error response:', err.response);

      // Handle different error formats
      let errorMessage = 'Failed to submit application';

      if (err.response?.data) {
        const errorData = err.response.data;

        // If message is an array of validation errors
        if (Array.isArray(errorData.message)) {
          errorMessage = (
            <ul className="mb-0">
              {errorData.message.map((msg, index) => (
                <li key={index}>{msg}</li>
              ))}
            </ul>
          );
        }
        // If message is a string
        else if (typeof errorData.message === 'string') {
          errorMessage = errorData.message;
        }
        // If error is a string
        else if (typeof errorData.error === 'string') {
          errorMessage = errorData.error;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handlePay = async () => {
    try {
      setSubmitting(true);
      const response = await initializeMembershipPayment(membershipStatus.membership.id);
      window.location.href = response.paymentUrl;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to initialize payment');
      setSubmitting(false);
    }
  };

  const renderMembershipCard = () => {
    if (!membershipStatus || !membershipStatus.membership) return null;

    const { membership } = membershipStatus;
    const daysRemaining = membership.daysRemaining || 0;

    return (
      <div className="membership-card">
        <div className="membership-header">
          <div>
            <h2>{membership.typeName} Membership</h2>
            <span className={`status-badge status-${membership.status}`}>
              {membership.statusName}
            </span>
          </div>
          {membership.code && (
            <div className="membership-code">
              <div className="code-label">Member Code</div>
              <div className="code-value">{membership.code}</div>
            </div>
          )}
        </div>

        <div className="membership-body">
          <div className="member-info">
            <div className="info-row">
              <span className="label">Name:</span>
              <span className="value">{membership.name}</span>
            </div>
            {membership.coupleName && (
              <div className="info-row">
                <span className="label">Partner:</span>
                <span className="value">{membership.coupleName}</span>
              </div>
            )}
            <div className="info-row">
              <span className="label">Email:</span>
              <span className="value">{membership.email}</span>
            </div>
            <div className="info-row">
              <span className="label">Phone:</span>
              <span className="value">{membership.phone}</span>
            </div>
          </div>

          {membership.startDate && membership.endDate && (
            <div className="membership-dates">
              <div className="date-info">
                <span className="label">Start Date:</span>
                <span className="value">
                  {new Date(membership.startDate).toLocaleDateString()}
                </span>
              </div>
              <div className="date-info">
                <span className="label">End Date:</span>
                <span className="value">
                  {new Date(membership.endDate).toLocaleDateString()}
                </span>
              </div>
              <div className="days-remaining">
                <span className={daysRemaining < 30 ? 'warning' : ''}>
                  {daysRemaining > 0 ? `${daysRemaining} days remaining` : 'Expired'}
                </span>
              </div>
            </div>
          )}

          {membership.qrCode && (
            <div className="membership-qr-section">
              <div className="qr-label">Scan QR Code</div>
              <img src={membership.qrCode} alt="Membership QR Code" className="qr-code-image-large" />
              <p className="qr-description">Scan this code to verify your membership</p>
            </div>
          )}
        </div>

        <div className="membership-footer">
          <div className="benefits">
            <h3>Your Benefits</h3>
            <ul>
              <li><i className="bi bi-check-circle-fill"></i> Priority reservations</li>
              <li><i className="bi bi-check-circle-fill"></i> Exclusive member shows</li>
              <li><i className="bi bi-check-circle-fill"></i> Special discounts</li>
              <li><i className="bi bi-check-circle-fill"></i> Personalized experiences</li>
            </ul>
          </div>
        </div>
      </div>
    );
  };

  const renderStatusMessage = () => {
    if (!membershipStatus) return null;

    if (membershipStatus.status === 'pending') {
      return (
        <div className="alert alert-info">
          <i className="bi bi-clock-history me-2"></i>
          <strong>Application Under Review</strong>
          <p>Your membership application is being reviewed by our team. You will receive an email notification once it has been processed.</p>
        </div>
      );
    }

    if (membershipStatus.status === 'approved') {
      return (
        <div className="alert alert-success">
          <i className="bi bi-check-circle me-2"></i>
          <strong>Application Approved!</strong>
          <p>Your membership has been approved. Please complete your payment to activate your membership.</p>
          <button className="btn btn-primary mt-3" onClick={handlePay} disabled={submitting}>
            {submitting ? 'Processing...' : 'Pay Now'}
          </button>
        </div>
      );
    }

    if (membershipStatus.status === 'pending_payment') {
      return (
        <div className="alert alert-warning">
          <i className="bi bi-credit-card me-2"></i>
          <strong>Payment Pending</strong>
          <p>Your payment is being processed. Your membership will be activated once approved.</p>
        </div>
      );
    }

    if (membershipStatus.status === 'rejected') {
      return (
        <div className="alert alert-danger">
          <i className="bi bi-x-circle me-2"></i>
          <strong>Application Not Approved</strong>
          <p>Unfortunately, your membership application was not approved at this time. You can apply again with updated information.</p>
          <button className="btn btn-primary mt-3" onClick={() => setShowForm(true)}>
            Apply Again
          </button>
        </div>
      );
    }

    if (membershipStatus.status === 'expired') {
      return (
        <div className="alert alert-secondary">
          <i className="bi bi-calendar-x me-2"></i>
          <strong>Membership Expired</strong>
          <p>Your membership has expired. You can apply for a new membership below.</p>
          <button className="btn btn-primary mt-3" onClick={() => setShowForm(true)}>
            Apply Again
          </button>
        </div>
      );
    }

    return null;
  };

  const renderApplicationForm = () => {
    return (
      <form onSubmit={handleSubmit} className="membership-form">
        <h2>Apply for Membership</h2>

        {error && (
          <div className="alert alert-danger">
            <i className="bi bi-exclamation-triangle me-2"></i>
            <strong>Please fix the following errors:</strong>
            <div className="mt-2">
              {error}
            </div>
          </div>
        )}

        <div className="section">
          <h3>Membership Type</h3>
          <div className="membership-types">
            <div className="type-card">
              <input
                type="radio"
                id="single"
                name="type"
                value="1"
                checked={formData.type === 1}
                onChange={(e) => setFormData({ ...formData, type: parseInt(e.target.value) })}
              />
              <label htmlFor="single">
                <div className="type-icon"><i className="bi bi-person"></i></div>
                <div className="type-name">Single</div>
                <div className="type-price">$150 USD / year</div>
              </label>
            </div>

            <div className="type-card">
              <input
                type="radio"
                id="couple"
                name="type"
                value="2"
                checked={formData.type === 2}
                onChange={(e) => setFormData({ ...formData, type: parseInt(e.target.value) })}
              />
              <label htmlFor="couple">
                <div className="type-icon"><i className="bi bi-people"></i></div>
                <div className="type-name">Couple</div>
                <div className="type-price">$250 USD / year</div>
              </label>
            </div>
          </div>
        </div>

        <div className="section">
          <h3>Personal Information</h3>
          <div className="row">
            <div className="col-md-6 mb-3">
              <label>Full Name *</label>
              <input
                type="text"
                className="form-control"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </div>
            <div className="col-md-6 mb-3">
              <label>Email *</label>
              <input
                type="email"
                className="form-control"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>
            <div className="col-md-6 mb-3">
              <label>Phone Number *</label>
              <PhoneInput
                country={'ae'}
                value={formData.phone}
                onChange={(value) => handlePhoneChange(value, 'phone')}
                inputClass="form-control"
              />
            </div>
            <div className="col-md-6 mb-3">
              <label>Social Media Link</label>
              <input
                type="text"
                className="form-control"
                name="socialMediaLink"
                value={formData.socialMediaLink}
                onChange={handleChange}
                placeholder="Instagram, Facebook, etc."
              />
            </div>
            <div className="col-md-6 mb-3">
              <label>Birthdate</label>
              <input
                type="date"
                className="form-control"
                name="birthdate"
                value={formData.birthdate}
                onChange={handleChange}
              />
            </div>
            <div className="col-md-6 mb-3">
              <label>Gender</label>
              <select className="form-control" name="gender" value={formData.gender} onChange={handleChange}>
                <option value="1">Male</option>
                <option value="2">Female</option>
              </select>
            </div>
          </div>
        </div>

        {formData.type === 2 && (
          <div className="section">
            <h3>Partner Information</h3>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label>Partner's Full Name *</label>
                <input
                  type="text"
                  className="form-control"
                  name="coupleName"
                  value={formData.coupleName}
                  onChange={handleChange}
                  required={formData.type === 2}
                />
              </div>
              <div className="col-md-6 mb-3">
                <label>Partner's Phone *</label>
                <PhoneInput
                  country={'ae'}
                  value={formData.couplePhone}
                  onChange={(value) => handlePhoneChange(value, 'couplePhone')}
                  inputClass="form-control"
                />
              </div>
              <div className="col-md-6 mb-3">
                <label>Partner's Social Media</label>
                <input
                  type="text"
                  className="form-control"
                  name="coupleSocialMediaLink"
                  value={formData.coupleSocialMediaLink}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>
        )}

        <div className="section">
          <h3>Address Information</h3>
          <div className="row">
            <div className="col-md-6 mb-3">
              <label>Street Address</label>
              <input
                type="text"
                className="form-control"
                name="addressStreet"
                value={formData.addressStreet}
                onChange={handleChange}
              />
            </div>
            <div className="col-md-6 mb-3">
              <label>Neighborhood</label>
              <input
                type="text"
                className="form-control"
                name="addressNeighborhood"
                value={formData.addressNeighborhood}
                onChange={handleChange}
              />
            </div>
            <div className="col-md-6 mb-3">
              <label>Country of Residence</label>
              <input
                type="text"
                className="form-control"
                name="residenceCountry"
                value={formData.residenceCountry}
                onChange={handleChange}
              />
            </div>
            <div className="col-md-6 mb-3">
              <label>Additional Info</label>
              <input
                type="text"
                className="form-control"
                name="addressMoreInfo"
                value={formData.addressMoreInfo}
                onChange={handleChange}
                placeholder="Apartment, floor, etc."
              />
            </div>
          </div>
        </div>

        <div className="section">
          <div className="alert alert-info">
            <i className="bi bi-info-circle me-2"></i>
            <strong>Next Step: Payment Authorization</strong>
            <p className="mb-0 mt-2">
              After submitting this form, you'll be redirected to securely authorize your payment.
              Your card will only be charged after admin approval of your membership application.
            </p>
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="btn btn-primary btn-lg" disabled={submitting}>
            {submitting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span>
                Processing...
              </>
            ) : (
              <>
                <i className="bi bi-arrow-right-circle me-2"></i>
                Continue to Payment
              </>
            )}
          </button>
        </div>
      </form>
    );
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="container my-5 text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="membership-page">
        <div className="container">
          <div className="page-header">
            <h1><i className="bi bi-award me-3"></i>Membership</h1>
            <p className="lead">Join the Tali Restaurants family and enjoy exclusive benefits</p>
          </div>

          {membershipStatus && membershipStatus.hasMembership && (
            <>
              {renderMembershipCard()}
              {renderStatusMessage()}
            </>
          )}

          {!membershipStatus?.hasMembership && renderStatusMessage()}

          {showForm && renderApplicationForm()}

          {!membershipStatus?.hasMembership && !membershipStatus?.canApply && !showForm && (
            <div className="alert alert-info">
              <i className="bi bi-info-circle me-2"></i>
              You cannot apply for membership at this time.
            </div>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Membership;
